import { useEffect, useState } from "react";

/**
 * Suspense fallback for lazy routes.
 * - First 400ms: nothing (avoids spinner flash on fast loads)
 * - 400ms–5s: subtle centered spinner with polite live-region text
 * - After 5s: adds "Still loading — this is taking longer than usual"
 * Fully accessible via role="status" + aria-live="polite".
 */
const RouteFallback = () => {
  const [phase, setPhase] = useState<"hidden" | "loading" | "stalled">("hidden");

  useEffect(() => {
    const t1 = window.setTimeout(() => setPhase("loading"), 400);
    const t2 = window.setTimeout(() => setPhase("stalled"), 5000);
    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
    };
  }, []);

  return (
    <div
      className="min-h-screen bg-background flex items-center justify-center"
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      {phase !== "hidden" && (
        <div className="flex flex-col items-center gap-4 text-center px-6">
          <div
            aria-hidden="true"
            className="w-10 h-10 rounded-full border-2 border-border border-t-steel-2 animate-spin"
          />
          <p className="font-mono text-xs uppercase tracking-[0.18em] text-ink-3 font-semibold">
            Loading
          </p>
          {phase === "stalled" && (
            <p className="text-sm text-muted-foreground max-w-[42ch]">
              Still loading — this is taking longer than usual. Please check your
              connection; the page will appear as soon as it&rsquo;s ready.
            </p>
          )}
        </div>
      )}
      <span className="sr-only">
        {phase === "stalled" ? "Still loading, please wait." : "Loading page."}
      </span>
    </div>
  );
};

export default RouteFallback;
